import React, { useEffect, useState } from 'react';
import { GroupDto, KindergartenDetailsDto, LocationDto } from '../../../../common/interfaces/kindergarten.interface';
import { ReactComponent as CircleIcon } from '../../../../assets/circle-icon.svg';
import { ReactComponent as AddIcon } from '../../../../assets/add-icon.svg';
import { ReactComponent as EditIcon } from '../../../../assets/edit-icon.svg';
import { ReactComponent as ListEmptyStateIcon } from '../../../../assets/list-empty-state-icon.svg';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { ActionMode, GeneralStatusEnum } from '../../../../common/enums/common.enum';
import GroupModal from '../../../../components/GroupModal';
import { useNavigate } from 'react-router-dom';
import LocationModal from '../LocationModal';
import { theme } from '../../../../theme';
import { getLocationsWIthGroups } from '../../../../api/kindergarten.api';
import { centeredContainerStyle, emptyStateContainerStyle, locationCardStyle } from './LocationsAndGroupsTab.styles';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';

interface LocationsAndGroupsTabProps {
  kindergarten: KindergartenDetailsDto;
}

const getStatusColor = (status?: string) => {
  return status === GeneralStatusEnum.ACTIVE ? '#97B66B' : theme.palette.text.secondary;
};

const LocationsAndGroupsTab: React.FC<LocationsAndGroupsTabProps> = ({ kindergarten }) => {
  const navigate = useNavigate();

  const [locations, setLocations] = useState<LocationDto[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [editGroup, setEditGroup] = useState<GroupDto | undefined>(undefined);

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationDto | null>(null);

  useEffect(() => {
    fetchLocations();
  }, [kindergarten.id]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await getLocationsWIthGroups(kindergarten.id);
      setLocations(response.results ?? []);
    } catch (error) {
      console.error('Failed to fetch locations and groups', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    navigate(`/${kindergarten.id}/locations/create/${encodeURIComponent(kindergarten.name)}`);
  };
  const handleAddGroupClick = (location: LocationDto) => {
    setEditGroup(undefined);
    setSelectedLocation(location);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };
  const handleGroupCreateOrUpdate = (group: GroupDto, mode: ActionMode) => {
    if (!selectedLocation) return;

    setLocations(prevLocations =>
      prevLocations.map(loc => {
        if (loc.id !== selectedLocation.id) return loc;

        const existingGroups = loc.groups ?? [];

        if (mode === ActionMode.CREATE) {
          return {
            ...loc,
            groups: [...existingGroups, group]
          };
        }

        if (mode === ActionMode.EDIT) {
          return {
            ...loc,
            groups: existingGroups.map(g => (g.id == group.id ? group : g))
          };
        }

        return loc;
      })
    );
  };

  const handleEditGroupClick = (group: GroupDto) => {
    setEditGroup(group);
    const locationForGroup = locations.find(loc => loc.groups?.some(g => g.id === group.id)) || null;
    setSelectedLocation(locationForGroup);
    setModalOpen(true);
  };

  const handleEditLocationClick = (location: LocationDto) => {
    setSelectedLocation(location);
    setLocationModalOpen(true);
  };

  const handleLocationUpdated = (updatedLocation: LocationDto) => {
    setLocations(prev => prev.map(loc => (loc.id === updatedLocation.id ? updatedLocation : loc)));
    setLocationModalOpen(false);
    setSelectedLocation(null);
  };

  if (!loading && locations.length === 0) {
    return (
      <Box sx={centeredContainerStyle}>
        <ListEmptyStateIcon style={{ fontSize: 40, color: 'text.secondary' }} />
        <Typography sx={{ fontWeight: 'bold' }}>No location created yet</Typography>
        <GenericButton
          buttonType={GenericButtonTypeEnum.Primary}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
          label="Create"
        />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: 24,
            color: 'text.primary',
            fontFamily: 'Poppins'
          }}
        >
          Locations & Groups
        </Typography>
        <GenericButton
          buttonType={GenericButtonTypeEnum.Primary}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
          label="Create"
        />
      </Stack>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {locations.map(location => (
          <Paper key={location.id} sx={locationCardStyle}>
            <Stack sx={{ px: 1.5, py: 2, backgroundColor: '#FBFBFC', borderRadius: '10px' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                <Stack direction="column" spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircleIcon
                      style={{
                        color: getStatusColor(location.status),
                        fontSize: 12
                      }}
                    />
                    <Typography
                      fontWeight="bold"
                      sx={theme => ({
                        color: theme.palette.primary.main,
                        fontSize: 14
                      })}
                    >
                      {location.name}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {location.address}
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <AddIcon
                    style={{
                      color: 'primary.main',
                      fontSize: 18,
                      cursor: 'pointer'
                    }}
                    onClick={() => handleAddGroupClick(location)}
                  />
                  <EditIcon
                    style={{
                      color: 'primary.main',
                      fontSize: 18,
                      cursor: 'pointer'
                    }}
                    onClick={() => handleEditLocationClick(location)}
                  />
                </Stack>
              </Stack>
            </Stack>

            <Box
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                width: '100%',
                mx: 0
              }}
            />
            <Box sx={{ flexGrow: 1, maxHeight: 56 * 4, overflowY: 'auto' }}>
              {location.groups && location.groups.length > 0 ? (
                location.groups.map((group, index) => (
                  <Stack
                    key={group.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{
                      height: 56,
                      px: 1.5,
                      borderRadius: 1,
                      textDecoration: 'underline',
                      borderBottom: index !== (location.groups?.length ?? 0) - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                    onClick={() =>
                      navigate(
                        `/${kindergarten.id}/groups/${group.id}/details/${encodeURIComponent(kindergarten.name)}/${encodeURIComponent(
                          location.name
                        )}`
                      )
                    }
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircleIcon
                        style={{
                          color: getStatusColor(group.status),
                          fontSize: 12
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'primary.main',
                          fontSize: 14
                        }}
                      >
                        {group.name}
                      </Typography>
                    </Stack>
                    <EditIcon
                      style={{
                        color: 'primary.main',
                        fontSize: 18,
                        cursor: 'pointer'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        handleEditGroupClick({
                          id: group.id,
                          name: group.name,
                          description: group.description,
                          status: group.status
                        });
                      }}
                    />
                  </Stack>
                ))
              ) : (
                <Box sx={emptyStateContainerStyle}>
                  <ListEmptyStateIcon style={{ fontSize: 32, color: 'text.secondary' }} />
                  <Typography sx={{ fontWeight: 'bold' }}>No group created yet</Typography>
                  <GenericButton
                    buttonType={GenericButtonTypeEnum.Primary}
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddGroupClick(location)}
                    label="Create"
                  />
                </Box>
              )}
            </Box>
          </Paper>
        ))}
      </Box>
      <GroupModal
        open={modalOpen}
        onClose={handleCloseModal}
        kindergartenId={kindergarten.id}
        locationId={selectedLocation?.id}
        locationStatus={selectedLocation?.status}
        group={editGroup}
        mode={editGroup ? ActionMode.EDIT : ActionMode.CREATE}
        onCreateOrUpdate={handleGroupCreateOrUpdate}
      />
      <LocationModal
        open={locationModalOpen}
        onClose={() => {
          setLocationModalOpen(false);
          setSelectedLocation(null);
        }}
        kindergartenId={kindergarten.id}
        location={selectedLocation}
        onSuccess={handleLocationUpdated}
      />
    </Box>
  );
};
export default LocationsAndGroupsTab;
